// AI behavior detection system for learning user patterns

import { logger } from "../utils/logger.ts";
import type {
  UIStateAPI,
  UserPreferencesAPI,
  UndoRedoAction,
  ToolName,
  Helper,
} from "../types/user-types";

export interface BehaviorPattern {
  readonly id: string;
  readonly pattern: string;
  readonly confidence: number; // 0-1
  readonly evidence: readonly string[];
  readonly timestamp: Date;
}

export interface BehaviorDetectorConfig {
  readonly minConfidence?: number;
  readonly maxMemories?: number;
  readonly detectionInterval?: number;
}

export interface BehaviorDetectorAPI {
  readonly analyzeSession: () => void;
  readonly getDetectedPatterns: () => readonly BehaviorPattern[];
  readonly forceAnalysis: () => void;
  // Naming pattern tracking
  readonly trackElementNaming: (
    elementName: string,
    elementType: string
  ) => void;
  readonly trackLayerNaming: (layerName: string, layerType: string) => void;
  readonly trackProjectNaming: (projectName: string) => void;
  readonly trackPageNaming: (pageName: string) => void;
  readonly destroy: () => void;
}

/**
 * Creates an AI behavior detector that learns user patterns
 *
 * @param userPrefs - User preferences manager
 * @param uiState - UI state manager
 * @param config - Detection configuration
 * @returns Behavior detector API
 *
 * @example
 * ```typescript
 * const detector = createBehaviorDetector(userPrefs, uiState, {
 *   minConfidence: 0.7,
 *   maxMemories: 20
 * });
 *
 * // Detector runs automatically, but you can force analysis
 * detector.forceAnalysis();
 * detector.destroy(); // Always call when done
 * ```
 */
export const createBehaviorDetector = (
  userPrefs: UserPreferencesAPI,
  uiState: UIStateAPI,
  config: BehaviorDetectorConfig = {}
): BehaviorDetectorAPI => {
  const {
    minConfidence = 0.6,
    maxMemories = 15,
    detectionInterval = 30000, // 30 seconds
  } = config;

  let detectionTimer: number | null = null;
  let sessionData: {
    toolUsage: Map<ToolName, number>;
    helperUsage: Map<Helper, number>;
    undoRedoCount: number;
    sessionStart: Date;
    namingPatterns: {
      layers: string[];
      elements: string[];
      projects: string[];
      pages: string[];
    };
    interactions: Array<{
      action: string;
      timestamp: Date;
      context: any;
    }>;
  } = {
    toolUsage: new Map(),
    helperUsage: new Map(),
    undoRedoCount: 0,
    sessionStart: new Date(),
    namingPatterns: {
      layers: [],
      elements: [],
      projects: [],
      pages: [],
    },
    interactions: [],
  };

  let detectedPatterns: BehaviorPattern[] = [];

  // Track tool changes
  const trackToolChange = (tool: ToolName, subtool?: string | null) => {
    sessionData.toolUsage.set(tool, (sessionData.toolUsage.get(tool) || 0) + 1);
    sessionData.interactions.push({
      action: "tool-change",
      timestamp: new Date(),
      context: { tool, subtool },
    });
  };

  // Track helper toggles
  const trackHelperToggle = (helpers: readonly Helper[]) => {
    helpers.forEach((helper) => {
      sessionData.helperUsage.set(
        helper,
        (sessionData.helperUsage.get(helper) || 0) + 1
      );
    });
    sessionData.interactions.push({
      action: "helper-toggle",
      timestamp: new Date(),
      context: { helpers },
    });
  };

  // Track undo/redo usage
  const trackUndoRedo = (action: UndoRedoAction | null) => {
    if (action) {
      sessionData.undoRedoCount++;
      sessionData.interactions.push({
        action: "undo-redo",
        timestamp: new Date(),
        context: { actionType: action.type },
      });
    }
  };

  // Track naming patterns
  const trackElementNaming = (elementName: string, elementType: string) => {
    if (elementName && elementName.trim()) {
      sessionData.namingPatterns.elements.push(elementName.trim());
      sessionData.interactions.push({
        action: "element-named",
        timestamp: new Date(),
        context: { name: elementName, type: elementType },
      });
    }
  };

  const trackLayerNaming = (layerName: string, layerType: string) => {
    if (layerName && layerName.trim()) {
      sessionData.namingPatterns.layers.push(layerName.trim());
      sessionData.interactions.push({
        action: "layer-named",
        timestamp: new Date(),
        context: { name: layerName, type: layerType },
      });
    }
  };

  const trackProjectNaming = (projectName: string) => {
    if (projectName && projectName.trim()) {
      sessionData.namingPatterns.projects.push(projectName.trim());
      sessionData.interactions.push({
        action: "project-named",
        timestamp: new Date(),
        context: { name: projectName },
      });
    }
  };

  const trackPageNaming = (pageName: string) => {
    if (pageName && pageName.trim()) {
      sessionData.namingPatterns.pages.push(pageName.trim());
      sessionData.interactions.push({
        action: "page-named",
        timestamp: new Date(),
        context: { name: pageName },
      });
    }
  };

  // Analyze patterns and generate insights
  const analyzePatterns = (): BehaviorPattern[] => {
    const patterns: BehaviorPattern[] = [];
    const currentPrefs = userPrefs.getPreferences();
    const currentState = uiState.getState();

    // Pattern 1: Dominant tool preference
    const mostUsedTool = Array.from(sessionData.toolUsage.entries()).reduce(
      (a, b) => (a[1] > b[1] ? a : b),
      ["select", 0] as [ToolName, number]
    );

    if (mostUsedTool[1] > 3) {
      patterns.push({
        id: "dominant-tool",
        pattern: `User frequently uses ${mostUsedTool[0]} tool`,
        confidence: Math.min(mostUsedTool[1] / 10, 1),
        evidence: [
          `Used ${mostUsedTool[0]} ${mostUsedTool[1]} times this session`,
        ],
        timestamp: new Date(),
      });
    }

    // Pattern 2: Helper usage patterns
    if (
      sessionData.helperUsage.has("grid") &&
      sessionData.helperUsage.get("grid")! > 2
    ) {
      patterns.push({
        id: "grid-preference",
        pattern: "User typically enables grid helper for precision work",
        confidence: 0.8,
        evidence: [
          "Grid toggled multiple times",
          "Indicates preference for structured layouts",
        ],
        timestamp: new Date(),
      });
    }

    // Pattern 3: Undo/redo patterns (indicates iterative workflow)
    if (sessionData.undoRedoCount > 5) {
      patterns.push({
        id: "iterative-workflow",
        pattern:
          "User follows iterative design approach with frequent revisions",
        confidence: Math.min(sessionData.undoRedoCount / 20, 1),
        evidence: [`${sessionData.undoRedoCount} undo/redo actions in session`],
        timestamp: new Date(),
      });
    }

    // Pattern 4: Theme preference timing
    if (currentPrefs.user.theme === "dark") {
      const hour = new Date().getHours();
      if (hour >= 18 || hour <= 6) {
        patterns.push({
          id: "evening-theme",
          pattern: "User prefers dark theme during evening/night hours",
          confidence: 0.7,
          evidence: ["Dark theme active during evening hours"],
          timestamp: new Date(),
        });
      }
    }

    // Pattern 5: Project naming conventions
    const projectName = currentState.current_project.toLowerCase();
    if (
      projectName.includes("mobile") &&
      currentState.helpers.includes("grid")
    ) {
      patterns.push({
        id: "mobile-grid-pattern",
        pattern: "User enables grid helper for mobile projects",
        confidence: 0.9,
        evidence: ["Mobile project with grid helper enabled"],
        timestamp: new Date(),
      });
    }

    // Pattern 6: Naming conventions analysis
    const analyzeNamingPatterns = () => {
      const namingPatterns: BehaviorPattern[] = [];

      // Element naming patterns
      if (sessionData.namingPatterns.elements.length >= 3) {
        const elementNames = sessionData.namingPatterns.elements;

        // Check for consistent casing patterns
        const camelCaseCount = elementNames.filter((name) =>
          /^[a-z][a-zA-Z0-9]*$/.test(name)
        ).length;
        const kebabCaseCount = elementNames.filter(
          (name) => /^[a-z0-9-]+$/.test(name) && name.includes("-")
        ).length;
        const descriptiveCount = elementNames.filter(
          (name) => name.split(/[\s-_]/).length > 1
        ).length;

        if (camelCaseCount / elementNames.length > 0.6) {
          namingPatterns.push({
            id: "camelcase-naming",
            pattern: "User prefers camelCase naming for elements",
            confidence: camelCaseCount / elementNames.length,
            evidence: [
              `${camelCaseCount}/${elementNames.length} elements use camelCase`,
            ],
            timestamp: new Date(),
          });
        }

        if (kebabCaseCount / elementNames.length > 0.6) {
          namingPatterns.push({
            id: "kebab-case-naming",
            pattern: "User prefers kebab-case naming for elements",
            confidence: kebabCaseCount / elementNames.length,
            evidence: [
              `${kebabCaseCount}/${elementNames.length} elements use kebab-case`,
            ],
            timestamp: new Date(),
          });
        }

        if (descriptiveCount / elementNames.length > 0.7) {
          namingPatterns.push({
            id: "descriptive-naming",
            pattern: "User provides descriptive, multi-word names for elements",
            confidence: descriptiveCount / elementNames.length,
            evidence: [
              `${descriptiveCount}/${elementNames.length} elements have descriptive names`,
            ],
            timestamp: new Date(),
          });
        }

        // Check for semantic naming patterns
        const uiTerms = [
          "button",
          "header",
          "footer",
          "nav",
          "menu",
          "card",
          "modal",
        ];
        const semanticCount = elementNames.filter((name) =>
          uiTerms.some((term) => name.toLowerCase().includes(term))
        ).length;

        if (semanticCount / elementNames.length > 0.5) {
          namingPatterns.push({
            id: "semantic-naming",
            pattern: "User uses semantic UI terminology in element names",
            confidence: semanticCount / elementNames.length,
            evidence: [
              `${semanticCount}/${elementNames.length} elements use semantic terms`,
            ],
            timestamp: new Date(),
          });
        }
      }

      // Layer naming patterns
      if (sessionData.namingPatterns.layers.length >= 2) {
        const layerNames = sessionData.namingPatterns.layers;
        const organizationalTerms = [
          "background",
          "content",
          "ui",
          "overlay",
          "base",
        ];
        const organizationalCount = layerNames.filter((name) =>
          organizationalTerms.some((term) => name?.toLowerCase().includes(term))
        ).length;

        if (organizationalCount / layerNames.length > 0.6) {
          namingPatterns.push({
            id: "organizational-layer-naming",
            pattern: "User organizes layers with functional naming conventions",
            confidence: organizationalCount / layerNames.length,
            evidence: [
              `${organizationalCount}/${layerNames.length} layers use organizational terms`,
            ],
            timestamp: new Date(),
          });
        }
      }

      // Project/Page naming patterns
      const allNames = [
        ...sessionData.namingPatterns.projects,
        ...sessionData.namingPatterns.pages,
      ];

      if (allNames.length >= 2) {
        const versioningCount = allNames.filter((name) =>
          /v\d+|version|draft|final|wip/.test(name.toLowerCase())
        ).length;

        if (versioningCount > 0) {
          namingPatterns.push({
            id: "versioning-naming",
            pattern: "User includes version information in project/page names",
            confidence: Math.min((versioningCount / allNames.length) * 2, 1),
            evidence: [`${versioningCount} names include version indicators`],
            timestamp: new Date(),
          });
        }

        const clientProjectCount = allNames.filter((name) =>
          /client|project|app|site|design/.test(name.toLowerCase())
        ).length;

        if (clientProjectCount / allNames.length > 0.5) {
          namingPatterns.push({
            id: "client-project-naming",
            pattern: "User includes project context in naming",
            confidence: clientProjectCount / allNames.length,
            evidence: [
              `${clientProjectCount}/${allNames.length} names include project context`,
            ],
            timestamp: new Date(),
          });
        }
      }

      return namingPatterns;
    };

    patterns.push(...analyzeNamingPatterns());

    return patterns.filter((p) => p.confidence >= minConfidence);
  };

  // Store high-confidence patterns as memories
  const storePatterns = (patterns: BehaviorPattern[]) => {
    const currentMemories = userPrefs.getPreferences().user.memories_for_llm;

    patterns.forEach((pattern) => {
      const memoryString = `${pattern.pattern} (confidence: ${Math.round(
        pattern.confidence * 100
      )}%)`;

      // Only store if not already exists and meets confidence threshold
      if (
        !currentMemories.includes(memoryString) &&
        pattern.confidence >= minConfidence
      ) {
        userPrefs.addMemory(memoryString);
      }
    });

    // Trim old memories if we exceed max
    const updatedMemories = userPrefs.getPreferences().user.memories_for_llm;
    if (updatedMemories.length > maxMemories) {
      const excess = updatedMemories.length - maxMemories;
      for (let i = 0; i < excess; i++) {
        const memoryToRemove = updatedMemories[i];
        if (memoryToRemove) {
          userPrefs.removeMemory(memoryToRemove);
        }
      }
    }
  };

  // Main analysis function
  const analyzeSession = () => {
    const patterns = analyzePatterns();
    detectedPatterns = patterns;
    storePatterns(patterns);

    logger.debug("stateSync", "🧠 Detected behavior patterns", {
      count: patterns.length,
    });
  };

  // Set up automatic analysis
  const startDetection = () => {
    detectionTimer = window.setInterval(analyzeSession, detectionInterval);
  };

  const forceAnalysis = () => {
    analyzeSession();
  };

  const getDetectedPatterns = (): readonly BehaviorPattern[] => {
    return [...detectedPatterns];
  };

  const destroy = () => {
    if (detectionTimer) {
      clearInterval(detectionTimer);
      detectionTimer = null;
    }
  };

  // Start automatic detection
  startDetection();

  // Set up event listeners to track behavior
  // Note: In real implementation, you'd wire these to your event bus
  // For now, these are placeholder methods showing the concept

  return {
    analyzeSession,
    getDetectedPatterns,
    forceAnalysis,
    trackElementNaming,
    trackLayerNaming,
    trackProjectNaming,
    trackPageNaming,
    destroy,
  };
};
